import React, { useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import PlanPrice from "./PlanPrice";

/**
 * Modal dialog for confirming plan upgrades.
 * Shows plan details and redirects to Stripe checkout on confirmation.
 *
 * Accessibility features:
 * - Focus trap keeps focus within modal
 * - ESC key closes modal
 * - Proper ARIA attributes for screen readers
 * - Focus returns to trigger element on close
 */
const UpgradeModal = ({ plan, onConfirm, onCancel }) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Store the previously focused element when modal opens
  useEffect(() => {
    if (plan) {
      previousActiveElement.current = document.activeElement;
    }
  }, [plan]);

  // Handle ESC key to close modal
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        onCancel();
      }

      // Focus trap: Tab and Shift+Tab cycle within modal
      if (event.key === "Tab" && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift+Tab: if on first element, go to last
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        }
        // Tab: if on last element, go to first
        else if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    },
    [onCancel],
  );

  // Set up event listeners and focus management
  useEffect(() => {
    if (!plan) return;

    // Add keydown listener
    document.addEventListener("keydown", handleKeyDown);

    // Focus the modal container for screen readers
    if (modalRef.current) {
      const firstButton = modalRef.current.querySelector("button");
      if (firstButton) {
        firstButton.focus();
      }
    }

    // Prevent body scroll while modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;

      // Return focus to previously focused element
      if (
        previousActiveElement.current &&
        previousActiveElement.current.focus
      ) {
        previousActiveElement.current.focus();
      }
    };
  }, [plan, handleKeyDown]);

  if (!plan) return null;

  return (
    <div
      className="billing-modal-overlay"
      onClick={onCancel}
      role="presentation"
    >
      <div
        ref={modalRef}
        className="billing-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-title"
        aria-describedby="upgrade-modal-description"
      >
        <h3 id="upgrade-modal-title" className="billing-modal-title">
          Confirm Upgrade
        </h3>
        <p id="upgrade-modal-description" className="billing-modal-text">
          You are about to upgrade to the <strong>{plan.name}</strong> plan.
        </p>
        <div className="billing-modal-details">
          <div className="billing-modal-price">
            <PlanPrice plan={plan} />
          </div>
          <ul className="billing-modal-features" aria-label="Plan features">
            {(plan.features || []).slice(0, 3).map((feature, idx) => (
              <li key={`feature-${plan.id}-${idx}`}>{feature}</li>
            ))}
          </ul>
        </div>
        <p className="billing-modal-note">
          You'll be redirected to Stripe to complete payment.
        </p>
        <div className="billing-modal-actions">
          <button
            className="billing-modal-btn billing-modal-btn-cancel"
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className="billing-modal-btn billing-modal-btn-confirm"
            onClick={onConfirm}
            type="button"
          >
            Continue to Payment
          </button>
        </div>
      </div>
    </div>
  );
};

UpgradeModal.propTypes = {
  plan: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.number,
    features: PropTypes.arrayOf(PropTypes.string),
  }),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default UpgradeModal;
